import { useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Form,
  Heading,
  InlineStack,
  Link,
  Modal,
  ModalBody,
  Paragraph,
  Stack,
  Strong,
  Text,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { Elements } from '@stripe/react-stripe-js';
import { formatCentsToUsd } from '@/utils/currency';
import { useAddonCardEntry } from '../hooks/use-addon-card-entry';
import { useAddonCheckout, type AddonCheckout } from '../hooks/use-addon-checkout';
import { useAddonPayment, type AddonPayment } from '../hooks/use-addon-payment';
import { useBillingStripePromise } from '../hooks/use-billing-stripe';
import {
  addonCadenceLabel,
  addonConfirmLabel,
  addonOffer,
  addonPrice,
  addonSubline,
  autoRechargeErrors,
  DEFAULT_AUTO_RECHARGE_SETTINGS,
  formatShortDate,
  prorationTodayLabel,
  type AddonKey,
  type AddonOffer,
  type AddonProration,
  type AddonRequest,
  type AfterLineRun,
  type AutoRechargeSettings,
} from '../utils/addon-purchase';
import { AddonCardSection } from './addon-card-section';
import { AutoRechargeBox } from './auto-recharge-box';
import { CheckList } from './check-list';
import * as styles from './addon-purchase.css';

const FEATURE_TERMS_URL = 'https://customer.io/legal/feature-terms';

export type AddonPurchaseModalData = {
  request: AddonRequest;
  proration?: AddonProration;
};

export type AddonPurchaseResult = {
  key: AddonKey;
  payment: AddonPayment;
  /** Whether the add-on is now on the subscription. A variant that sells nothing yet only collects a card. */
  isPurchased: boolean;
  autoRecharge?: AutoRechargeSettings;
};

export function AddonPurchaseModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<AddonPurchaseModalData, AddonPurchaseResult>) {
  const stripe = useBillingStripePromise();
  const checkout = useAddonCheckout();

  return (
    <Modal
      data-test-addon-purchase-modal
      state={modalState}
      title={addonOffer(data.request).title}
      isDismissable={!checkout.isSubmitting}
    >
      <ModalBody>
        <Elements stripe={stripe}>
          <AddonPurchaseForm data={data} checkout={checkout} onConfirm={onConfirm} onClose={onClose} />
        </Elements>
      </ModalBody>
    </Modal>
  );
}

type AddonPurchaseFormProps = {
  data: AddonPurchaseModalData;
  checkout: AddonCheckout;
  onConfirm: (result: AddonPurchaseResult) => void;
  onClose: () => void;
};

function AddonPurchaseForm({ data, checkout, onConfirm, onClose }: AddonPurchaseFormProps) {
  const offer = addonOffer(data.request);
  const cardEntry = useAddonCardEntry();
  const payment = useAddonPayment(cardEntry);
  const autoRecharge = useAutoRecharge(offer);
  const isAutoRechargeInvalid = autoRecharge.isActive && autoRecharge.isInvalid;

  const onSubmit = async () => {
    if (payment.isBlocked || isAutoRechargeInvalid) return;
    const outcome = await checkout.confirm(data.request.key, payment.resolve);
    if (outcome == null) return;
    onConfirm({
      key: data.request.key,
      payment: outcome.payment,
      isPurchased: outcome.isPurchased,
      autoRecharge: autoRecharge.isActive ? autoRecharge.settings : undefined,
    });
  };

  return (
    <Form onSubmit={onSubmit}>
      <Stack gap="300">
        <Text color="secondary">{addonSubline(offer.cadence)}</Text>
        <OfferBlock offer={offer} proration={data.proration}>
          {offer.hasAutoRecharge ? (
            <AutoRechargeBox
              settings={autoRecharge.settings}
              errors={autoRecharge.errors}
              rechargeCents={offer.priceCents}
              onChange={autoRecharge.setSettings}
            />
          ) : null}
        </OfferBlock>
        {cardEntry.isFormOpen ? null : <GoodToKnow items={offer.goodToKnow} />}
        <AddonCardSection entry={cardEntry} cardError={payment.cardError} />
        <FinePrint />
        <Footer
          confirmLabel={addonConfirmLabel(offer, autoRecharge.isActive)}
          isConfirmDisabled={payment.isBlocked || isAutoRechargeInvalid}
          isSubmitting={checkout.isSubmitting}
          blockReason={payment.blockReason}
          purchaseError={checkout.error}
          onClose={onClose}
        />
      </Stack>
    </Form>
  );
}

function useAutoRecharge(offer: AddonOffer) {
  const [settings, setSettings] = useState(DEFAULT_AUTO_RECHARGE_SETTINGS);
  const errors = autoRechargeErrors(settings, offer.priceCents);
  return {
    settings,
    setSettings,
    errors,
    isActive: offer.hasAutoRecharge && settings.isEnabled,
    isInvalid: errors.threshold != null || errors.monthlyLimit != null,
  };
}

type OfferBlockProps = {
  offer: AddonOffer;
  proration?: AddonProration;
  children?: ReactNode;
};

function OfferBlock({ offer, proration, children }: OfferBlockProps) {
  return (
    <Stack gap="200" className={styles.offerBlock} data-test-addon-offer>
      <Stack gap="050">
        <Heading level="3" size="sm">
          {offer.offerName}
        </Heading>
        <InlineStack gap="100" alignBlock="baseline">
          <Text size="xxxl" weight="bold" data-test-addon-price>
            {addonPrice(offer)}
          </Text>
          <Text color="secondary" size="md">
            {addonCadenceLabel(offer.cadence)}
          </Text>
        </InlineStack>
      </Stack>
      <AfterLine runs={offer.afterLine} />
      {offer.cadence === 'monthly' && proration != null ? (
        <ProrationTable proration={proration} priceCents={offer.priceCents} />
      ) : null}
      {children}
    </Stack>
  );
}

type AfterLineProps = {
  runs: readonly AfterLineRun[];
};

function AfterLine({ runs }: AfterLineProps) {
  return (
    <Paragraph color="secondary" data-test-addon-after-line>
      {runs.map((run, index) => {
        const key = String(index);
        if (run === 'arrow') return <span key={key}>→</span>;
        return run.isStrong === true ? <Strong key={key}>{run.text}</Strong> : <span key={key}>{run.text}</span>;
      })}
    </Paragraph>
  );
}

type ProrationTableProps = {
  proration: AddonProration;
  priceCents: number;
};

function ProrationTable({ proration, priceCents }: ProrationTableProps) {
  return (
    <Stack gap="150" data-test-addon-proration>
      <Divider />
      <Flex justifyContent="space-between" gap="200">
        <Text color="secondary">{prorationTodayLabel(proration)}</Text>
        <Text color="secondary">{formatCentsToUsd(proration.proratedCents)}</Text>
      </Flex>
      <Box className={styles.prorationDivider} />
      <Flex justifyContent="space-between" gap="200">
        <Text weight="bold">Then every month from {formatShortDate(proration.nextBillingDate)}</Text>
        <Text weight="bold">{formatCentsToUsd(priceCents)}</Text>
      </Flex>
      <Text color="secondary" size="sm">
        Prorated from your billing date: you only pay for the days left in this cycle.
      </Text>
    </Stack>
  );
}

type GoodToKnowProps = {
  items: readonly string[];
};

function GoodToKnow({ items }: GoodToKnowProps) {
  return (
    <Stack gap="150" data-test-addon-good-to-know>
      <Heading level="3" size="sm">
        Good to know
      </Heading>
      <CheckList items={items} />
    </Stack>
  );
}

function FinePrint() {
  return (
    <Stack>
      <Paragraph color="secondary" size="xs">
        Add-ons are fixed monthly line items billed to your card, never from your credit balance.
      </Paragraph>
      <Paragraph color="secondary" size="xs">
        By continuing you agree to our{' '}
        <Link href={FEATURE_TERMS_URL} variant="secondary" isExternal={true}>
          terms
        </Link>
        .
      </Paragraph>
    </Stack>
  );
}

type FooterProps = {
  confirmLabel: string;
  isConfirmDisabled: boolean;
  isSubmitting: boolean;
  blockReason: string;
  purchaseError?: string;
  onClose: () => void;
};

function Footer({ blockReason, purchaseError, ...buttons }: FooterProps) {
  return (
    <Stack gap="150">
      {purchaseError == null ? null : (
        <Text color="critical" size="sm" role="alert" data-test-addon-purchase-error>
          {purchaseError}
        </Text>
      )}
      {blockReason === '' ? null : (
        <Text color="secondary" size="sm" data-test-addon-block-reason>
          {blockReason}
        </Text>
      )}
      <FooterButtons {...buttons} />
    </Stack>
  );
}

type FooterButtonsProps = Omit<FooterProps, 'blockReason' | 'purchaseError'>;

function FooterButtons({ confirmLabel, isConfirmDisabled, isSubmitting, onClose }: FooterButtonsProps) {
  return (
    <Flex justifyContent="flex-end" gap="200">
      <Button data-test-addon-cancel variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {/* Also disabled for an invalid auto-recharge input, under the costly-submit exception: confirm
          commits a card charge, SPE-4558 asks for it, and the rule line says what is wrong. */}
      <Button
        data-test-addon-confirm
        variant="primary"
        type="submit"
        isDisabled={isConfirmDisabled}
        isLoading={isSubmitting}
      >
        {confirmLabel}
      </Button>
    </Flex>
  );
}
